import { Box, Container, Stack, Typography, Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import BaseLanguages from 'components/AuthenticatedLayout/Sidebar/components/Languages'
import LiveChat from './components/LiveChat2'

const LOGO_WIFIRST = '/images/center_logo.svg'
const MOBILE_LOGO = '/images/wifirst_center_mobile_logo.png'
const APP_STORE_URL = 'https://apps.apple.com/fr/app/wifirst-center/id6447067001'
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=fr.wifirst.mycenter&hl=fr'

export interface IAuthBasePageWrapperProps {
  footer?: React.ReactNode | null
}

const MobileBanner = () => {
  const handleAppStoreClick = () => {
    window.open(APP_STORE_URL, '_blank')
  }

  const handleGooglePlayClick = () => {
    window.open(GOOGLE_PLAY_URL, '_blank')
  }

  return (
    <Box
      sx={{
        display: { xs: 'flex', md: 'none' },
        width: '100%',
        borderBottom: '1px solid #EFEFEF',
        py: 2,
        px: 2,
        background: 'white',
        boxShadow: '0px 1.85px 3.15px 0px #00000014, 0px 8.15px 6.52px 0px #0000000A, 0px 20px 13px 0px #0000000A'
      }}
    >
      <Container maxWidth="sm">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={1.5}>
            <img
              src={MOBILE_LOGO}
              alt="Wifirst Center Mobile"
              style={{ height: '40px', width: 'auto' }}
            />
            <Stack>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, lineHeight: 1.2 }}>
                Wifirst CENTER
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#989898', lineHeight: 1.2 }}>
                Ouvrir dans l'application.
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" gap={0.5}>
            <Button
              onClick={handleAppStoreClick}
              sx={{
                background: '#292929',
                color: 'white',
                height: '32px',
                borderRadius: '24px',
                px: 1.5,
                minWidth: 'auto',
                fontSize: '10.5px',
                fontWeight: 400,
                textTransform: 'none',
                '&:hover': {
                  background: '#404040'
                }
              }}
            >
              <i className="fa-brands fa-apple" style={{ fontSize: '14px' }} />
            </Button>
            <Button
              onClick={handleGooglePlayClick}
              sx={{
                background: '#292929',
                color: 'white',
                height: '32px',
                borderRadius: '24px',
                px: 1.5,
                minWidth: 'auto',
                fontSize: '10.5px',
                fontWeight: 400,
                textTransform: 'none',
                '&:hover': {
                  background: '#404040'
                }
              }}
            >
              <i className="fa-brands fa-google-play" style={{ fontSize: '12px' }} />
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
}

const Languages = () => {
  return (
    <Box
      sx={{
        top: { xs: 100, md: 50 },
        right: 25,
        position: 'absolute',
        width: '45px',
        height: '30',
        borderRadius: '16px',
        border: '1px solid #F0F0F0',
        background: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <BaseLanguages position={'bottom'} />
    </Box>
  )
}

const AuthBasePageWrapper = ({ children, footer }: React.PropsWithChildren<IAuthBasePageWrapperProps>) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  useEffect(() => {
    const el = document.getElementsByClassName('tf-v1-sidetab-button')[0] as HTMLElement
    if (el) el.style.display = 'none'
  }, [])

  const handleGoHome = () => {
    navigate('/')
  }

  const handleAppStoreClick = () => {
    window.open(APP_STORE_URL, '_blank')
  }

  const handleGooglePlayClick = () => {
    window.open(GOOGLE_PLAY_URL, '_blank')
  }

  return (
    <Box height={'100vh'} width={'100vw'} sx={{ background: '#F9F9F9' }}>
      <MobileBanner />
      <Container
        maxWidth="sm"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: { xs: 'calc(100vh - 72px)', md: '100vh' }
        }}
      >
        <Languages />
        <Box sx={{ height: '15%', display: 'flex', alignItems: 'center', pt: 4, pb: 3 }}>
          <Stack
            gap={1}
            id="logo_stack"
            direction="row"
            onClick={handleGoHome}
            sx={{ cursor: 'pointer' }}
          >
            <Box>
              <img
                src={LOGO_WIFIRST}
                alt="Logo Wifirst"
                className="logo"
                style={{ width: '132px', height: '24px', objectFit: 'contain' }}
              />
            </Box>
          </Stack>
        </Box>

        <Stack sx={{ alignItems: 'center' }}>
          {children}

          <Stack direction="row" gap={1.5} sx={{ mt: 3 }}>
            <Button
              onClick={handleAppStoreClick}
              sx={{
                background: '#292929',
                color: 'white',
                height: '40px',
                borderRadius: '24px',
                fontSize: '10.5px',
                fontWeight: 400,
                textTransform: 'none',
                px: 2,
                '&:hover': {
                  background: '#404040'
                }
              }}
            >
              <i className="fa-brands fa-apple" style={{ marginRight: '6px', fontSize: '14px' }} />
              App Store
            </Button>
            <Button
              onClick={handleGooglePlayClick}
              sx={{
                background: '#292929',
                color: 'white',
                height: '40px',
                borderRadius: '24px',
                fontSize: '10.5px',
                fontWeight: 400,
                textTransform: 'none',
                px: 2,
                '&:hover': {
                  background: '#404040'
                }
              }}
            >
              <i className="fa-brands fa-google-play" style={{ marginRight: '6px', fontSize: '12px' }} />
              Google Play
            </Button>
          </Stack>

          {footer}
          <LiveChat />
        </Stack>

        <Box sx={{ height: '20%', display: 'flex', alignItems: 'center' }}>
          <Stack sx={{ fontSize: '12px', color: 'content.tertiary' }}>
            <Box display={'flex'} gap={0.5} justifyContent={'center'}>
              <Typography variant="caption">{`${t('login.helper_new')}`}</Typography>
              <Typography variant="caption" dangerouslySetInnerHTML={{ __html: t('login.helper_new_2_bold') }}></Typography>
            </Box>
            <Box display={'flex'} gap={0.5} justifyContent={'center'}>
              <Typography variant="caption">{`${t('login.helper_5')} ${t('login.helper_6')}`}</Typography>
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}

export default AuthBasePageWrapper